// Small activity map for the profile pages: one person's outings inside a
// rolling date window, drawn as tracks plus pins, with the window picked
// independently of the paginated outing lists on the same page.
//
// Unlike MiniMap this keeps a single MapLibre instance across window changes —
// switching windows only calls setData and refits the camera, so there is no
// style reload flicker while flipping through the ranges.
import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl, { LngLatBounds, Map as MlMap, type MapGeoJSONFeature } from "maplibre-gl";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { gqlRequest } from "../lib/gql";
import { PROFILE_MAP, type ProfileMapOuting, type ProfileMapResult } from "../lib/queries";
import { featureCollection, toFeature, type Feature } from "../lib/geo";
import { useT, type MessageKey } from "../lib/i18n";
import { EmptyState, ErrorBox, Spinner } from "../components/ui";
import { useResolvedTheme } from "../lib/theme";
import { basemapStyle } from "./basemap";
import { COLORS } from "./layers";
import { MapTooltip, type TooltipState } from "./MapTooltip";

const LINE_SOURCE = "profile-lines";
const PIN_SOURCE = "profile-pins";
const INTERACTIVE = ["profile-line-hit", "profile-pins"];

// A rolling window rather than a calendar one: "last 30 days" means 30 days
// back from today, so the map matches what a user just did rather than what
// happens to fall in the current month. `null` days = all time.
export type MapWindow = { key: string; labelKey: MessageKey; days: number | null };

export const MAP_WINDOWS: MapWindow[] = [
  { key: "30d", labelKey: "profileMap.w30d", days: 30 },
  { key: "3m", labelKey: "profileMap.w3m", days: 91 },
  { key: "12m", labelKey: "profileMap.w12m", days: 365 },
  { key: "all", labelKey: "profileMap.wAll", days: null },
];

const DEFAULT_WINDOW = "12m";

/** ISO date `days` before today, or undefined for the all-time window. */
function windowStart(days: number | null): string | undefined {
  if (days == null) return undefined;
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function extendBounds(bounds: LngLatBounds, coords: unknown): void {
  if (!Array.isArray(coords)) return;
  if (coords.length >= 2 && typeof coords[0] === "number" && typeof coords[1] === "number") {
    bounds.extend([coords[0] as number, coords[1] as number]);
    return;
  }
  for (const c of coords) extendBounds(bounds, c);
}

/**
 * One line per outing: its own recorded track when there is one, otherwise the
 * simplified geometry of each linked route. Outings logged against a route
 * without a GPX would otherwise show as a bare pin.
 */
function outingLines(outings: ProfileMapOuting[]): Feature[] {
  const features: Feature[] = [];
  for (const o of outings) {
    const props = { kind: "outing", id: o.id, name: o.title, activity: o.activity, date: o.date };
    if (o.track?.geom) {
      const f = toFeature(o.track.geom, props);
      if (f) features.push(f);
      continue;
    }
    for (const link of o.outingRoutesByOutingId.nodes) {
      const f = toFeature(link.route.mainGeom, props);
      if (f) features.push(f);
    }
  }
  return features;
}

export function ProfileMap({ personId, className }: { personId: string; className?: string }) {
  const { t } = useT();
  const [windowKey, setWindowKey] = useState(DEFAULT_WINDOW);
  const win = MAP_WINDOWS.find((w) => w.key === windowKey) ?? MAP_WINDOWS[0];
  const from = windowStart(win.days);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["profile-map", personId, win.key],
    // `from: null` is a no-op predicate on the pdbq side, which is exactly the
    // all-time window — no separate document needed.
    queryFn: () => gqlRequest<ProfileMapResult>(PROFILE_MAP, { id: personId, from: from ?? null, first: 100 }),
    placeholderData: (prev) => prev,
  });

  const outings = useMemo(
    () => (data?.outingPeople.nodes ?? []).map((n) => n.outing),
    [data],
  );
  const lines = useMemo(() => outingLines(outings), [outings]);
  const pins = useMemo(
    () =>
      outings
        .map((o) => toFeature(o.point, { kind: "outing", id: o.id, name: o.title, activity: o.activity, date: o.date }))
        .filter(Boolean) as Feature[],
    [outings],
  );

  return (
    <div className={className}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold tracking-tight text-foreground">
          {t("profileMap.title")}
          <span className="ml-2 font-normal text-muted-foreground">
            {t(outings.length === 1 ? "profileMap.outingsOne" : "profileMap.outingsOther", { count: outings.length })}
          </span>
        </h2>
        <div className="flex items-center gap-1.5">
          {isFetching && (
            <span
              aria-label={t("common.loading")}
              className="size-3.5 animate-spin rounded-full border-2 border-muted border-t-primary"
            />
          )}
          <div className="inline-flex overflow-hidden rounded-lg border">
            {MAP_WINDOWS.map((w) => (
              <button
                key={w.key}
                type="button"
                onClick={() => setWindowKey(w.key)}
                aria-pressed={w.key === windowKey}
                className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                  w.key === windowKey
                    ? "bg-primary text-primary-foreground"
                    : "bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                }`}
              >
                {t(w.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </div>
      {error != null ? (
        <ErrorBox error={error} />
      ) : isLoading ? (
        <Spinner />
      ) : lines.length === 0 && pins.length === 0 ? (
        <EmptyState>{t("profileMap.empty")}</EmptyState>
      ) : (
        <ProfileMapCanvas lines={lines} pins={pins} />
      )}
    </div>
  );
}

function ProfileMapCanvas({ lines, pins }: { lines: Feature[]; pins: Feature[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  // A counter rather than a boolean: the map is re-created when the theme
  // flips, and the data effect must re-run for the new instance even though a
  // boolean "ready" would already be true.
  const [loadCount, setLoadCount] = useState(0);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [, navigate] = useLocation();
  const navRef = useRef(navigate);
  navRef.current = navigate;
  const theme = useResolvedTheme();

  useEffect(() => {
    if (!containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: basemapStyle(theme),
      center: [10, 46.5],
      zoom: 4,
      attributionControl: { compact: true },
      cooperativeGestures: true,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    map.on("load", () => {
      map.addSource(LINE_SOURCE, { type: "geojson", data: featureCollection([]) });
      map.addSource(PIN_SOURCE, { type: "geojson", data: featureCollection([]) });
      map.addLayer({
        id: "profile-lines",
        type: "line",
        source: LINE_SOURCE,
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": COLORS.route, "line-width": 2.5, "line-opacity": 0.75 },
      });
      // A wide transparent line on top so thin tracks are still easy to hover
      // and click at mini-map zooms.
      map.addLayer({
        id: "profile-line-hit",
        type: "line",
        source: LINE_SOURCE,
        paint: { "line-color": COLORS.route, "line-width": 12, "line-opacity": 0 },
      });
      map.addLayer({
        id: "profile-pins",
        type: "circle",
        source: PIN_SOURCE,
        paint: {
          "circle-color": COLORS.outing,
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 4, 12, 7],
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
        },
      });

      for (const layerId of INTERACTIVE) {
        map.on("mousemove", layerId, (e) => {
          const feature = e.features?.[0] as MapGeoJSONFeature | undefined;
          if (!feature) return;
          map.getCanvas().style.cursor = "pointer";
          setTooltip({ x: e.point.x, y: e.point.y, feature: feature.properties ?? {} });
        });
        map.on("mouseleave", layerId, () => {
          map.getCanvas().style.cursor = "";
          setTooltip(null);
        });
        map.on("click", layerId, (e) => {
          const feature = e.features?.[0];
          if (!feature) return;
          const { kind, id } = feature.properties as { kind: string; id: string };
          navRef.current(`/${kind}/${id}`);
        });
      }
      setLoadCount((n) => n + 1);
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // Re-created only on theme flips; data and camera flow in through the
    // effect below.
  }, [theme]);

  // Refresh both sources and refit the camera whenever the window changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || loadCount === 0) return;
    (map.getSource(LINE_SOURCE) as maplibregl.GeoJSONSource | undefined)?.setData(featureCollection(lines));
    (map.getSource(PIN_SOURCE) as maplibregl.GeoJSONSource | undefined)?.setData(featureCollection(pins));
    setTooltip(null);

    const bounds = new LngLatBounds();
    for (const f of [...lines, ...pins]) extendBounds(bounds, f.geometry.coordinates);
    if (!bounds.isEmpty()) map.fitBounds(bounds, { padding: 32, maxZoom: 12, duration: 600 });
  }, [loadCount, lines, pins]);

  return (
    <div className="relative h-64 w-full overflow-hidden rounded-xl border shadow-card">
      <div ref={containerRef} className="h-full w-full" />
      <MapTooltip tooltip={tooltip} />
    </div>
  );
}
